import React, { useEffect, useContext } from 'react'
import ContentfulQuery from '#src/containers/Contentful'
import { useExperiment, Experiment } from '@deal/experiment-js'
import { Button, Modal } from '@deal/components'
import { EngagementChannelCategoryFragment } from '#src/generated/graphql'
import { ContentfulResponse } from '../'
import { setLandingPageKey } from '#src/services/registrationValues'
import { ExperimentPlacements } from '../../EngagementChannelHandler'
import { AnalyticsContext } from '#src/containers/Analytics'
import useBreakpoint from '#src/hooks/useBreakpoint'
import styles from './styles.css'
import BackgroundImage from '#src/components/BackgroundImage'

interface V1Props {
  placement: ExperimentPlacements
  category: EngagementChannelCategoryFragment
  onSetCookie: () => void
  onDismissModal: (contentfulVariation: string) => void
  onSubmitModal: (categorySlug: string | null, contentfulVariation: string) => void
}

const V1: React.SFC<V1Props> = ({
  category,
  onDismissModal,
  onSubmitModal,
  onSetCookie,
  placement
}) => {
  const analytics = useContext(AnalyticsContext)
  const isMobile = !useBreakpoint('sm')
  const categorySlug = category.slug
  const fullPageTakeoverImage = category.contentAssets.fullPageTakeover
    ? category.contentAssets.fullPageTakeover.imageUrl
    : null

  // Used to separate the two placements and use one contentful type, e.g. `winter-sports-search`
  const categorySlugAndPlacement = `${categorySlug}-${placement}`
  const { result, loading: experimentLoading } = useExperiment({
    experiment: `fpt-${categorySlugAndPlacement}`,
    defaultTreatment: 'off'
  })

  /**
   * This defaults to category slug and placement or uses the experiment result
   * if the exxperiment isn't killed or off. This requires the result be set up
   * in Contentful with a matching variation.
   */
  let contentfulVariation = categorySlugAndPlacement
  if (result && result !== 'control' && result !== 'off') {
    contentfulVariation = result
  }

  useEffect(() => {
    // Wait for the experiment to resolve before executing this hook
    if (result) {
      analytics?.events.engagementChannelViewed({
        engagement_channel: 'full-page-takeover',
        variation: contentfulVariation,
        category: categorySlug
      })
    }
  }, [result])

  // Override the cookie set at the engagement channel handler level to include the variation specficity
  // Example cookie value: full-page-takeover-winter-sports-product-v1
  setLandingPageKey(`full-page-takeover-${contentfulVariation}`)

  if (experimentLoading) {
    return null
  }

  return (
    <ContentfulQuery template="fullPageTakeover" variation={contentfulVariation}>
      {({ content, loading }: ContentfulResponse) => {
        if (content) {
          return (
            <Modal isOpen className={styles.takeover} isScrollLockActive={!isMobile}>
              <BackgroundImage
                containerClassName={styles.background}
                size="100vw"
                src={fullPageTakeoverImage || ''}
              >
                <div className={styles.gradientMask} />

                <div className={styles.content}>
                  <div className={styles.header}>{content.header}</div>

                  <Experiment id="full-page-takeover-button-width" default="full-width">
                    {({ loading, result }) => {
                      if (loading) {
                        return null
                      }

                      const buttonWrapperClass =
                        result === 'full-width' ? styles.fullWidthAction : styles.normalWidthAction

                      return (
                        <div className={buttonWrapperClass}>
                          <Button
                            size="large"
                            onClick={() => onSubmitModal(categorySlug, contentfulVariation)}
                          >
                            {content.buttonText ? content.buttonText : 'Get started'}
                          </Button>
                        </div>
                      )
                    }}
                  </Experiment>

                  <div className={styles.steps}>
                    {content.questions.map((question, idx) => (
                      <div className={styles.step} key={idx}>
                        <div className={styles.stepHeader}>{question.title}</div>
                        <div className={styles.stepBody}>{question.subtext}</div>
                      </div>
                    ))}
                  </div>

                  <div className={styles.dismiss}>
                    <Button
                      variant="secondary-link"
                      onClick={() => onDismissModal(contentfulVariation)}
                    >
                      {content.dismissButtonText ? content.dismissButtonText : 'Maybe later'}
                    </Button>
                  </div>
                </div>
              </BackgroundImage>
            </Modal>
          )
        } else if (!loading && !content) {
          /**
           * In the event there isn't content set up for the full page takeover for this
           * category (and we're on the search page), we want to set the cookie. We don't
           * want the module to popup if they switch to a category that has a contentful
           * entry.
           */
          if (placement === 'search') {
            onSetCookie()
          }
        }

        return null
      }}
    </ContentfulQuery>
  )
}

export default V1
